
import { useState } from 'react';
import type { Order } from '@/types/order';
import { ORDER_STATUS_CONFIG } from '@/types/order';

interface OrderCardProps {
  order: Order;
  onViewDetail: (order: Order) => void;
}

const planColors: Record<string, string> = {
  basic: 'bg-gray-100 text-gray-600',
  standard: 'bg-[#FDF5EA] text-[#C9A96E]',
  premium: 'bg-[#2D2D2D] text-white',
};

export default function OrderCard({ order, onViewDetail }: OrderCardProps) {
  const [imgError, setImgError] = useState<Record<number, boolean>>({});
  const status = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.pending;

  // Derive planId from productName for color styling
  const planId = order.productName?.includes('프리미엄')
    ? 'premium'
    : order.productName?.includes('스탠다드')
      ? 'standard'
      : 'basic';
  const planColor = planColors[planId] || planColors.basic;

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => onViewDetail(order)}
    >
      {/* Header */}
      <div className="px-6 py-5 flex items-center justify-between border-b border-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium text-gray-400 truncate">{order.orderId.length > 16 ? `${order.orderId.slice(0, 16)}…` : order.orderId}</span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 ${planColor}`}>
            {order.productName}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium shrink-0 ${status.color}`}>
          <i className={`${status.icon} text-xs`}></i>
          {order.statusLabel || status.label}
        </span>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {/* Styles */}
            {order.selections && order.selections.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2 font-medium tracking-wide uppercase">선택 스타일</p>
                <div className="flex flex-wrap gap-2">
                  {order.selections.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-[#FDF5EA] rounded-full px-3 py-1">
                      <span className="text-xs text-[#2D2D2D] font-medium">{s.styleName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="flex items-center gap-5 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <i className="ri-image-line"></i>
                사진 {order.photoCount}장
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {order.createDt?.split(' ')[0]}
              </span>
              {order.status === 'completed' && order.completedAt && (
                <span className="flex items-center gap-1 text-green-600">
                  <i className="ri-check-double-line"></i>
                  완료 {order.completedAt.split(' ')[0]}
                </span>
              )}
              {order.status === 'processing' && order.estimatedDelivery && (
                <span className="flex items-center gap-1 text-amber-600">
                  <i className="ri-truck-line"></i>
                  예상 완료 {order.estimatedDelivery}
                </span>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="text-right shrink-0">
            <p className="text-xl font-bold text-[#2D2D2D]">{order.amount.toLocaleString()}원</p>
          </div>
        </div>
      </div>

    </div>
  );
}
